import { createContext, useContext, useEffect } from 'react'

/**
 * Edit-on-behalf-Kontext: eine Editor-Seite (admin/brand-admin bearbeitet eine
 * *fremde* vCard) meldet dem `EditorLayout` den Zielnutzer + das Rücksprungziel,
 * damit das Layout den Kontext-Banner zeigt. Bei der *eigenen* vCard (`/editor/me`)
 * registriert die Seite nichts → kein Banner.
 */
export interface EditSession {
  /** Anzeige-Label des Zielnutzers (Name o. urlKey der fremden vCard). */
  targetLabel: string
  /** Rücksprungziel (Herkunftspfad o. Default-Liste). */
  backTo: string
}

interface EditSessionRegistry {
  register: (session: EditSession | null) => void
}

const Ctx = createContext<EditSessionRegistry | null>(null)

/** Vom `EditorLayout` gestellt — hält den aktiven Edit-on-behalf-Kontext. */
export const EditSessionProvider = Ctx.Provider

/**
 * Meldet dem `EditorLayout` den Edit-on-behalf-Kontext der aktuellen Seite.
 * `null` (oder Aufruf mit `null`) = eigene vCard → kein Banner. Räumt beim
 * Unmount / Seitenwechsel automatisch auf. No-op außerhalb eines Layout-Providers
 * (z. B. wenn eine Seite in Tests standalone gerendert wird).
 */
export function useRegisterEditSession(session: EditSession | null): void {
  const registry = useContext(Ctx)
  const targetLabel = session?.targetLabel ?? null
  const backTo = session?.backTo ?? null
  useEffect(() => {
    if (!registry) return
    registry.register(targetLabel != null && backTo != null ? { targetLabel, backTo } : null)
    return () => registry.register(null)
  }, [registry, targetLabel, backTo])
}
